import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Spacing } from "./design-tokens";

const buttonVariants = cva(
    "inline-flex relative items-center cursor-pointer justify-center gap-2 whitespace-nowrap rounded-default text-default-color text-default font-bold leading-default px-default transition-colors disabled:pointer-events-none disabled:bg-input-disabled disabled:text-disabled",
    {
        variants: {
            appearance: {
                neutral:
                    "bg-neutral-default hover:bg-neutral-hover active:bg-neutral-press",
                brand: "text-inverse bg-brand-bold-primary-default hover:bg-brand-bold-primary-hover active:bg-brand-bold-primary-press",
                link: "text-link-default hover:text-link-hover active:text-link-press",
                warning:
                    "bg-warning-default hover:bg-warning-hover active:bg-warning-press",
                danger: "text-inverse bg-danger-bold-default hover:bg-danger-bold-hover active:bg-danger-bold-press",
                subtle: "bg-neutral-subtlest-default hover:bg-neutral-subtlest-hover active:bg-neutral-subtlest-press",
                "subtle link":
                    "text-subtle hover:underline  hover:underline-offset-4 active:text-default-color",
                navigation:
                    "bg-neutral-subtle-default hover:bg-neutral-subtle-hover active:bg-neutral-subtle-press",
                none: "bg-transparent p-0",
            },
            spacing: {
                default: "min-h-default",
                compact: "min-h-compact",
                relaxed: "min-h-relaxed",
                none: "min-h-0 p-0 px-0 py-0",
            },
            isSelected: {
                false: null,
                true: "bg-selected-default text-selected",
            },
        },
        defaultVariants: { appearance: "neutral", spacing: undefined },
    },
);

type ButtonBaseProps<E extends React.ElementType> = {
    appearance?:
        | "neutral"
        | "brand"
        | "link"
        | "warning"
        | "danger"
        | "subtle"
        | "subtle link"
        | "navigation"
        | "none";
    spacing?: Spacing;
    isLoading?: boolean;
    isSelected?: boolean;
    asChild?: boolean;
    as?: E;
};

export type ButtonProps<E extends React.ElementType> = ButtonBaseProps<E> &
    React.ComponentProps<E>;

const Button = <E extends React.ElementType = "button">({
    className,
    appearance = "neutral",
    spacing = "default",
    isSelected,
    isLoading,
    asChild = false,
    as,
    ...props
}: ButtonProps<E>) => {
    const Comp = asChild ? Slot : as || "button";
    return (
        <Comp
            {...props}
            disabled={props.disabled || isLoading}
            className={cn(
                buttonVariants({ appearance, spacing, isSelected }),
                className,
            )}
        />
    );
};
Button.displayName = "Button";

export { Button, buttonVariants };
